/**
 * MedicBlu dropdown menu pattern — birebir kopya.
 *
 * Pattern:
 *   <dropdown-menu-disclosure follow-summary-link trigger="hover">
 *     <details class="header__menu-disclosure">
 *       <summary data-follow-link="/url"> Title </summary>
 *       <ul> ... </ul>
 *     </details>
 *   </dropdown-menu-disclosure>
 *
 * Class hierarchy:
 *   CustomDetails (HTMLElement) — summary click preventDefault + follow-link navigate
 *   MenuDisclosure (CustomDetails) — hover/click + outside click + escape + focusout
 *   DropdownMenuDisclosure (MenuDisclosure) — WAAPI stagger animation
 *
 * Submenu links: HİÇBİR custom handler. Browser native <a href> navigation.
 */
(function () {
  if (window.__noendyDropdownMenuLoaded) return;
  window.__noendyDropdownMenuLoaded = true;

  class CustomDetails extends HTMLElement {
    constructor() {
      super();
      this._onSummaryClickedListener = this._onSummaryClicked.bind(this);
    }

    static get observedAttributes() {
      return ['open', 'aria-expanded'];
    }

    connectedCallback() {
      const details = this.disclosureElement;
      const summary = this.summaryElement;
      if (!details || !summary) return;
      details.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      summary.addEventListener('click', this._onSummaryClickedListener);
    }

    disconnectedCallback() {
      if (this.summaryElement) {
        this.summaryElement.removeEventListener('click', this._onSummaryClickedListener);
      }
    }

    get disclosureElement() {
      return this.querySelector('details');
    }

    get summaryElement() {
      const details = this.disclosureElement;
      return details ? details.firstElementChild : null;
    }

    get contentElement() {
      const details = this.disclosureElement;
      return details ? details.lastElementChild : null;
    }

    toggle(force) {
      const current = this.disclosureElement && this.disclosureElement.getAttribute('aria-expanded') === 'true';
      const newValue = typeof force === 'boolean' ? force : !current;
      if (newValue) {
        this.open();
      } else {
        this.close();
      }
    }

    open({ instant = false } = {}) {
      const details = this.disclosureElement;
      if (!details || details.getAttribute('aria-expanded') === 'true') return;
      details.open = true;
      details.setAttribute('aria-expanded', 'true');
      this._createShowAnimation(instant);
    }

    close() {
      const details = this.disclosureElement;
      if (!details) return;
      details.setAttribute('aria-expanded', 'false');
      if (!details.open) return;
      details.removeAttribute('open');
    }

    _createShowAnimation(/* instant */) { /* override */ }
    _createHideAnimation() { /* override */ return null; }

    _onSummaryClicked(event) {
      event.preventDefault();
      const details = this.disclosureElement;
      const summary = this.summaryElement;
      if (details && details.open && summary && summary.hasAttribute('data-follow-link')) {
        window.location.href = summary.getAttribute('data-follow-link');
        return;
      }
      this.toggle();
    }
  }

  class MenuDisclosure extends CustomDetails {
    constructor() {
      super();
      this._hoverTimer = null;
      this._detectClickOutsideListener = this._detectClickOutside.bind(this);
      this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
      this._detectFocusOutListener = this._detectFocusOut.bind(this);
      this._detectHoverOutsideListener = this._detectHoverOutside.bind(this);
      this._detectHoverListener = this._detectHover.bind(this);
      this._handleKeyboardListener = this._handleKeyboard.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      const details = this.disclosureElement;
      if (!details) return;
      details.addEventListener('mouseover', this._detectHoverListener);
      details.addEventListener('mouseout', this._detectHoverListener);
      details.addEventListener('keydown', this._handleKeyboardListener);

      // Drawer width'i measure et → details'e CSS variable olarak set
      // (--noendy-drawer-w ile underline ::after drawer ile birebir aynı genişlikte olur)
      const measureDrawerWidth = () => {
        const content = this.contentElement;
        if (!content) return;
        const w = content.offsetWidth || content.getBoundingClientRect().width;
        if (w > 0) {
          details.style.setProperty('--noendy-drawer-w', `${w}px`);
        }
      };
      // Initial measure + ResizeObserver ile dinamik update
      requestAnimationFrame(measureDrawerWidth);
      if (typeof ResizeObserver !== 'undefined' && this.contentElement) {
        const ro = new ResizeObserver(measureDrawerWidth);
        ro.observe(this.contentElement);
        this._drawerResizeObserver = ro;
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      const details = this.disclosureElement;
      if (details) {
        details.removeEventListener('mouseover', this._detectHoverListener);
        details.removeEventListener('mouseout', this._detectHoverListener);
        details.removeEventListener('keydown', this._handleKeyboardListener);
      }
    }

    get trigger() {
      if (!window.matchMedia('screen and (pointer: fine)').matches) return 'click';
      return this.getAttribute('trigger') || 'hover';
    }

    get mouseOverDelayTolerance() {
      return 250;
    }

    open({ instant = false } = {}) {
      super.open({ instant });
      document.addEventListener('click', this._detectClickOutsideListener);
      document.addEventListener('keydown', this._detectEscKeyboardListener);
      document.addEventListener('focusout', this._detectFocusOutListener);
      document.addEventListener('mouseover', this._detectHoverOutsideListener);
    }

    close() {
      super.close();
      document.removeEventListener('click', this._detectClickOutsideListener);
      document.removeEventListener('keydown', this._detectEscKeyboardListener);
      document.removeEventListener('focusout', this._detectFocusOutListener);
      document.removeEventListener('mouseover', this._detectHoverOutsideListener);
    }

    _handleKeyboard(event) {
      if (event.key === 'Escape') {
        this.close();
        if (this.summaryElement) this.summaryElement.focus();
      }
    }

    _detectClickOutside(event) {
      if (this.trigger !== 'click') return;
      const closestDetails = event.target.closest && event.target.closest('details');
      const closestMenu = closestDetails ? closestDetails.parentElement : null;
      if (!this.contains(event.target) && !(closestMenu instanceof MenuDisclosure)) {
        this.toggle(false);
      }
    }

    _detectHover(event) {
      if (this.trigger !== 'hover') return;
      if (event.type === 'mouseover') {
        clearTimeout(this._hoverTimer);
        this.toggle(true);
      } else if (event.type === 'mouseout') {
        this._hoverTimer = setTimeout(() => this.toggle(false), this.mouseOverDelayTolerance);
      }
    }

    _detectHoverOutside(event) {
      if (this.trigger !== 'hover') return;
      const closestDetails = event.target.closest && event.target.closest('details');
      const closestMenu = closestDetails ? closestDetails.parentElement : null;
      if (closestMenu instanceof MenuDisclosure && closestMenu !== this && !closestMenu.contains(this) && !this.contains(closestMenu)) {
        clearTimeout(this._hoverTimer);
        // Sibling switch — eski drawer anında kapanır, yeni açılır
        this.toggle(false);
      }
    }

    _detectEscKeyboard(event) {
      if (event.code === 'Escape') {
        const targetMenu = event.target.closest && event.target.closest('details[open]');
        const menuParent = targetMenu ? targetMenu.parentElement : null;
        if (menuParent instanceof MenuDisclosure) {
          menuParent.toggle(false);
          event.stopPropagation();
        }
      }
    }

    _detectFocusOut(event) {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.toggle(false);
      }
    }
  }

  class DropdownMenuDisclosure extends MenuDisclosure {
    _createShowAnimation(instant) {
      const content = this.contentElement;
      if (!content || instant) return;

      if (content.animate) {
        content.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 250, easing: 'ease', fill: 'forwards' }
        );

        const items = content.querySelectorAll(':scope > li');
        items.forEach((li, idx) => {
          try {
            li.animate(
              [
                { opacity: 0, transform: 'translateY(8px)' },
                { opacity: 1, transform: 'translateY(0)' }
              ],
              {
                duration: 150,
                delay: idx * 100,
                easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                fill: 'both'
              }
            );
          } catch (e) { /* ignore */ }
        });
      }
    }

    _createHideAnimation() {
      // MedicBlu pattern: drawer opacity 1→0 transparent fade-out (CSS transition).
      // Items animation cancel ETME — son state'te kalsınlar, drawer opacity ile blend olsunlar.
      return null;
    }
  }

  if (!window.customElements.get('dropdown-menu-disclosure')) {
    window.customElements.define('dropdown-menu-disclosure', DropdownMenuDisclosure);
  }
})();
